/**
 * Basketball Position Recommendation Rules
 * 
 * Defines position recommendations for Basketball players based on their attributes
 */

import type { PositionRule } from '../positionRecommendations';

/**
 * Basketball Position Rules
 */
export const BASKETBALL_POSITION_RULES: PositionRule[] = [
  // PREMIUM POSITIONS - Require elevated thresholds
  {
    position: 'PG',
    tier: 'premium',
    description: 'Point Guard - Requires elite ball handling, passing, and court vision',
    requirements: [
      { attribute: 'dribbling', minValue: 7, required: true },
      { attribute: 'passing', minValue: 7, required: true },
      { attribute: 'speed', minValue: 6, required: false },
      { attribute: 'agility', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const dribbling = attrs.get('dribbling') || 0;
      const passing = attrs.get('passing') || 0;
      const speed = attrs.get('speed') || 0;
      const agility = attrs.get('agility') || 0;
      const awareness = attrs.get('awareness') || 0;
      const sport_iq = attrs.get('sport_iq') || 0;
      return dribbling * 1.5 + passing * 1.5 + (speed + agility) * 1.0 + awareness * 1.2 + sport_iq * 1.2;
    },
  },
  {
    position: 'C',
    tier: 'premium',
    description: 'Center - Requires height, rebounding, and defensive presence',
    requirements: [
      { attribute: 'rebounding', minValue: 7, required: true },
      { attribute: 'blocking', minValue: 6, required: true },
      { attribute: 'strength', minValue: 7, required: false },
    ],
    scoreFormula: (attrs) => {
      const rebounding = attrs.get('rebounding') || 0;
      const blocking = attrs.get('blocking') || 0;
      const defense = attrs.get('defense') || 0;
      const strength = attrs.get('strength') || 0; // Physical attribute
      return rebounding * 1.8 + blocking * 1.5 + defense * 1.3 + strength * 1.2;
    },
  },

  // STANDARD POSITIONS - Moderate thresholds
  {
    position: 'SG',
    tier: 'standard',
    description: 'Shooting Guard - Requires shooting and perimeter skills',
    requirements: [
      { attribute: 'shooting', minValue: 7, required: true },
      { attribute: 'dribbling', minValue: 6, required: true },
      { attribute: 'speed', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const shooting = attrs.get('shooting') || 0;
      const dribbling = attrs.get('dribbling') || 0;
      const speed = attrs.get('speed') || 0;
      const agility = attrs.get('agility') || 0;
      const passing = attrs.get('passing') || 0;
      return shooting * 1.8 + dribbling * 1.3 + (speed + agility) * 1.0 + passing * 0.8;
    },
  },
  {
    position: 'SF',
    tier: 'standard',
    description: 'Small Forward - Versatile wing player, needs balanced skills',
    requirements: [
      { attribute: 'shooting', minValue: 6, required: true },
      { attribute: 'defense', minValue: 6, required: true },
      { attribute: 'rebounding', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const shooting = attrs.get('shooting') || 0;
      const defense = attrs.get('defense') || 0;
      const rebounding = attrs.get('rebounding') || 0;
      const dribbling = attrs.get('dribbling') || 0;
      const speed = attrs.get('speed') || 0;
      const agility = attrs.get('agility') || 0;
      return shooting * 1.3 + defense * 1.3 + rebounding * 1.0 + dribbling * 1.0 + (speed + agility) * 0.9;
    },
  },
  {
    position: 'PF',
    tier: 'standard',
    description: 'Power Forward - Requires rebounding, strength, and inside scoring',
    requirements: [
      { attribute: 'rebounding', minValue: 6, required: true },
      { attribute: 'strength', minValue: 6, required: true },
      { attribute: 'defense', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const rebounding = attrs.get('rebounding') || 0;
      const strength = attrs.get('strength') || 0;
      const defense = attrs.get('defense') || 0;
      const blocking = attrs.get('blocking') || 0;
      const shooting = attrs.get('shooting') || 0;
      return rebounding * 1.6 + strength * 1.4 + defense * 1.2 + blocking * 1.1 + shooting * 0.8;
    },
  },

  // EASIER POSITIONS — accessible for less-developed players, ensuring anyone
  // with attributes recorded always receives at least one recommendation.
  {
    position: 'PF',
    tier: 'easier',
    description: 'Power Forward - Accessible for players with size who can rebound',
    requirements: [
      { attribute: 'rebounding', minValue: 4, required: false },
      { attribute: 'strength', minValue: 4, required: false },
    ],
    scoreFormula: (attrs) => {
      const rebounding = attrs.get('rebounding') || 0;
      const strength = attrs.get('strength') || 0;
      const defense = attrs.get('defense') || 0;
      const blocking = attrs.get('blocking') || 0;
      return rebounding * 1.6 + strength * 1.4 + defense * 1.0 + blocking * 0.9;
    },
  },
  {
    position: 'SF',
    tier: 'easier',
    description: 'Small Forward - Versatile entry-level wing, minimal thresholds',
    requirements: [
      { attribute: 'defense', minValue: 4, required: false },
      { attribute: 'rebounding', minValue: 4, required: false },
    ],
    scoreFormula: (attrs) => {
      const defense = attrs.get('defense') || 0;
      const rebounding = attrs.get('rebounding') || 0;
      const shooting = attrs.get('shooting') || 0;
      const dribbling = attrs.get('dribbling') || 0;
      return defense * 1.4 + rebounding * 1.3 + shooting * 1.1 + dribbling * 1.0;
    },
  },
];

